import { useQuery } from '@tanstack/react-query';
import { Link, useRouterState } from '@tanstack/react-router';
import type { ReactNode } from 'react';
import { ChoixTheme, Logo } from '../ui';
import { MenuCompte } from '../compte/MenuCompte';
import { BadgeAcces } from '../abonnement/BadgeAcces';
import { RappelAbonnement } from '../abonnement/RappelAbonnement';
import { useSession } from '../auth/session';
import { useDossierActif } from '../dossier/dossierActif';
import { nomDuDossier } from '../dossier/prenoms';
import type { Dossier as DossierType } from '../dossier/types';
import { ChoixLangue } from '../i18n/ChoixLangue';
import { useTraduction } from '../i18n/langue';
import { versionLisible } from '../version';
import type { Messages } from '@repere/core';

/**
 * Les écrans dont le titre ne change jamais en cours de route — un seul `<h1>` du début à
 * la fin de la visite, contrairement au dossier (dont le nom dépend d'une réponse encore
 * en vol) ou à « Rejoindre »/« Supprimer un dossier » (dont le titre change avec l'issue
 * du geste). Pour ceux-là, le bandeau peut se contenter de relire la même clé de
 * traduction que l'écran lui-même — la même phrase, jamais recopiée.
 *
 * Volontairement incomplet : mieux vaut garder le mot-symbole sur un écran non couvert
 * que d'y afficher un titre resté figé sur ce qu'il était à l'arrivée.
 */
const TITRES_STATIQUES: Record<string, (messages: Messages) => string> = {
  '/dossiers/creer': (messages) => messages.creerDossier.titre,
  '/compte': (messages) => messages.compte.monCompte,
  '/options': (messages) => messages.compte.optionsTitre,
};

/**
 * Cadre de l'application.
 *
 * Rien ici n'indique la présence, la disponibilité ou l'activité de l'autre parent :
 * ni « en ligne », ni « vu à », ni « dernière connexion ». Cette absence est un choix
 * de produit, pas un manque (docs/01 §5.1 et §5.2).
 */
/**
 * Le back-office n'est pas le produit.
 *
 * Sans cette exception, `/b-admin` héritait de tout le décor parent : le badge de formule,
 * le rappel d'abonnement et l'aide en ligne. Un administrateur en train de prolonger
 * l'abonnement de quelqu'un se serait vu proposer de souscrire — et l'aide en ligne aurait
 * ouvert un fil de discussion depuis un écran d'exploitation.
 *
 * Comparaison sur la fin du chemin : l'application est servie tantôt à la racine, tantôt
 * sous un préfixe, et une égalité stricte se serait cassée au premier changement d'hôte.
 */
const estBackOffice = (chemin: string): boolean => /\/b-admin\/?$/.test(chemin);

export function Coquille({ children }: { children: ReactNode }) {
  const { client, profil } = useSession();
  const { locale, messages } = useTraduction();
  const chemin = useRouterState({ select: (etat) => etat.location.pathname });

  /**
   * L'écran ouvert donne son titre au bandeau, à la place du mot-symbole.
   *
   * Le titre était un `<h1>` en tête du contenu, sous un bandeau qui répétait
   * « Repère-kids » sur tous les écrans une fois connecté. Deux lignes de titre l'une sur
   * l'autre, dont une qui n'apprend rien à qui est déjà dans l'application : sur
   * téléphone, elles coûtaient le tiers de la hauteur avant la première information utile.
   *
   * Le dossier ouvert reste un cas à part : son nom dépend d'une réponse encore en vol, et
   * se lit avec **la même clé que l'écran** (`['case-file', id]`) — le bandeau ne déclenche
   * aucune requête de plus, il lit ce que l'écran a déjà demandé. Les autres écrans dont
   * le titre ne bouge pas viennent de `TITRES_STATIQUES`, sans requête du tout.
   *
   * Tant qu'aucun des deux ne répond, le mot-symbole reste — sur l'accueil, sur les écrans
   * non couverts, et le temps qu'une réponse arrive : un bandeau qui clignote du nom du
   * produit au titre de l'écran serait pire que l'un ou l'autre.
   */
  const dossierActif = useDossierActif();
  const dossier = useQuery({
    queryKey: ['case-file', dossierActif],
    queryFn: () => client.request<DossierType>(`/case-files/${dossierActif}`),
    enabled: Boolean(dossierActif && profil),
  });
  const titreDossier = dossier.data
    ? nomDuDossier(dossier.data.label, dossier.data.children, dossier.data.custodyKind)
    : null;

  const routeActuelle = useRouterState({
    select: (etat) => etat.matches.at(-1)?.routeId ?? null,
  });
  const titreStatique = routeActuelle ? TITRES_STATIQUES[routeActuelle]?.(messages) : undefined;

  const titreEcran = titreDossier ?? titreStatique ?? null;

  if (estBackOffice(chemin)) {
    return (
      <>
        <a className="saut-contenu" href="#contenu">
          {messages.coquille.sautContenu}
        </a>
        <main className="coquille" id="contenu">
          {children}
        </main>
      </>
    );
  }

  return (
    <>
      <a className="saut-contenu" href="#contenu">
        {messages.coquille.sautContenu}
      </a>

      <header className="application">
        <div className="coquille">
          {/* `Link` et non `<a href="/">` : sous `/app`, la racine est la
              vitrine — le lien ejectait donc du produit, avec rechargement
              complet. `Link` respecte le `basepath` du routeur et reste une
              navigation cote client. */}
          {/* Le menu ouvre le bandeau, à gauche de la marque.

              Il était à l'opposé, contre le bord droit. Sur téléphone, la seule commande
              de l'en-tête se trouvait alors dans le coin le plus difficile à atteindre du
              pouce, à la diagonale de la main qui tient l'appareil — et c'est celle qui
              porte l'urgence. À gauche, elle est sur le trajet du pouce, et elle ouvre la
              lecture au lieu de la fermer : on rencontre les commandes avant le contenu,
              comme dans tout ce que le parent utilise par ailleurs.

              Il est aussi **premier dans le balisage**, donc premier à la tabulation :
              l'ordre du clavier suit l'ordre de l'œil, sans qu'aucun `tabindex` n'ait à le
              rattraper. */}
          <div className="entete-gauche">
            {profil ? <MenuCompte /> : null}
            {/* Le badge est posé en indice du symbole, pas du texte : il dit le régime du
                **compte**, pas de l'écran affiché à côté. Le texte change désormais à
                chaque écran — le mot-symbole, un titre de dossier, un titre d'écran — et
                un badge qui l'aurait suivi se serait déplacé, agrandi, tronqué avec lui.
                Le symbole seul est stable, présent identique sur tous les écrans : c'est
                le seul point fixe où l'accrocher. */}
            <div className="marque-bloc">
              <Link className="marque" to="/">
                <span className="marque-symbole">
                  <Logo />
                  <BadgeAcces />
                </span>
                {/* Une fois dans l'application, le titre de l'écran prend la place du
                    mot-symbole. Le lien reste celui de la marque — il ramène à la liste
                    des dossiers, ce qui est exactement ce qu'on attend d'un titre
                    d'application sur lequel on appuie. Le titre se replie sur deux lignes
                    si besoin (`.marque-titre`) : coupé dès la première, « Garde alternée
                    de Manon et Chloé » devenait « Garde alternée de M… », et le bandeau
                    cessait de dire de quel écran il s'agit. `title` garde le texte entier
                    pour les cas extrêmes. */}
                <span className="marque-texte">
                  {titreEcran ? (
                    <span className="marque-titre" title={titreEcran}>
                      {titreEcran}
                    </span>
                  ) : (
                    <span className="marque-nom">
                      Repère<span className="marque-suffixe">-kids</span>
                    </span>
                  )}
                </span>
              </Link>
            </div>
          </div>
          {/* La langue et l'apparence ont déménagé dans « Options », derrière le menu du
              compte : deux réglages qu'on touche trois fois dans sa vie occupaient en
              permanence le bandeau de tous les écrans, où ils se disputaient la largeur
              avec la marque, le badge de formule et l'adresse du compte.

              Ils restent ici **tant qu'on n'est pas connecté**, et c'est la seule raison
              pour laquelle ils y étaient : un parent invité qui arrive dans la mauvaise
              langue doit pouvoir en changer sur l'écran de connexion, où « Options » ne
              lui est pas encore accessible.

              L'apparence, elle, n'a plus besoin d'être ici sur téléphone
              (`.entete-droite .choix-theme` sous 600 px) : le thème choisi — ou celui de
              l'appareil, par défaut — est déjà posé par le script d'amorçage avant que
              React ne rende quoi que ce soit (voir `themeConserve` dans `ui/Theme.tsx`).
              L'écran de connexion s'affiche donc déjà sombre le soir ; le bouton ne
              servait plus qu'à revenir sur un choix qu'on fait une fois dans sa vie, au
              prix d'une rangée qui débordait sur deux lignes sur les téléphones étroits.
              Il reste sur grand écran, où la place ne manque pas.

              Ils restent à droite : ce ne sont pas des entrées du menu, et les faire
              suivre la marque à gauche les collerait au nom du produit. */}
          {profil ? null : (
            <div className="entete-droite">
              <ChoixLangue />
              <ChoixTheme />
            </div>
          )}
        </div>
      </header>

      <main className="coquille" id="contenu">
        {children}
      </main>

      {/* La sollicitation périodique de la version gratuite. Elle vit dans la coquille, à
          côté du contenu et non dedans : posée dans un écran, elle se serait rejouée à
          chaque changement d'onglet. Elle ne se rend qu'à son échéance, et seulement pour
          un compte gratuit avéré. */}
      <RappelAbonnement />

      {/* L'aide en ligne n'est plus montée ici : elle a quitté sa pastille flottante pour
          une entrée du menu de l'en-tête, qui la monte lui-même (voir `MenuCompte`). Elle
          reste joignable de partout — le menu est dans la coquille — mais elle ne recouvre
          plus le bas de chaque écran. */}

      <footer className="application">
        <div className="coquille">
          <p>
            <a href="https://le-lab.net" target="_blank" rel="noreferrer noopener">
              le-lab.net
            </a>{' '}
            {messages.coquille.signature}
          </p>
          <p className="discret">
            <time dateTime={__BUILD_TIME__}>{versionLisible(locale, messages)}</time>
          </p>
        </div>
      </footer>
    </>
  );
}
